/**
 * SEO Components
 * JSON-LD structured data for better search engine visibility
 */
import { Helmet } from 'react-helmet';

// Organization Schema
export const OrganizationSchema = () => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Art By Arawol",
        "alternateName": "Arawol The White Wallaby",
        "url": "https://arawol.art",
        "logo": "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/x06wcv5o_Asset%206.png",
        "description": "Authentic Aboriginal artwork by Elder Colin Isaacs - Gadigal and Bidjigal man",
        "founder": {
          "@type": "Person",
          "name": "Colin Isaacs",
          "jobTitle": "Artist & Elder"
        },
        "sameAs": [
          "https://www.instagram.com/arawol_art",
          "https://www.facebook.com/arawolart"
        ]
      })}
    </script>
  </Helmet>
);

// Product Schema
export const ProductSchema = ({ product }) => {
  if (!product) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "description": product.description || `Authentic Aboriginal artwork by Colin Isaacs`,
    "image": product.images?.[0] || product.image,
    "brand": {
      "@type": "Brand",
      "name": "Art By Arawol"
    },
    "artist": {
      "@type": "Person",
      "name": "Colin Isaacs"
    },
    "offers": {
      "@type": "Offer",
      "url": `https://arawol.art/products/${product.product_id}`,
      "priceCurrency": "AUD",
      "price": product.base_price || product.variants?.[0]?.price || 0,
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Art By Arawol"
      }
    }
  };

  // Add limited edition info if applicable
  if (product.is_limited_edition) {
    schema.isLimitedEdition = true;
    schema.additionalProperty = {
      "@type": "PropertyValue",
      "name": "Edition Size",
      "value": product.edition_size || "Limited"
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Article Schema for Blog Posts
export const ArticleSchema = ({ article }) => {
  if (!article) return null;
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt || article.content?.substring(0, 160),
          "image": article.image,
          "author": {
            "@type": "Person",
            "name": article.author || "Art By Arawol"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Art By Arawol",
            "logo": {
              "@type": "ImageObject",
              "url": "https://customer-assets.emergentagent.com/job_d3d35e52-10c3-4c88-ba0b-938139dca4c0/artifacts/x06wcv5o_Asset%206.png"
            }
          },
          "datePublished": article.created_at,
          "dateModified": article.updated_at || article.created_at
        })}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url
        }))
      })}
    </script>
  </Helmet>
);

export default { OrganizationSchema, ProductSchema, ArticleSchema, BreadcrumbSchema };
